<script setup>
/**
 * RED-262: candidate info card for the model-explorer map.
 *
 * Renders the human-most-important comparison fields for a candidate
 * transaction (or the subject property). Used in two modes:
 *  - `compact`  → the hover tooltip summary (a few key stats)
 *  - full       → the click-to-open detail card (the whole breakdown)
 * Set `subject` when the passed object is the subject property rather than a
 * candidate (different field shape).
 *
 * Pure content card — the popup chrome (close button, positioning) lives in the
 * parent (ModelExplorer.vue), which anchors it via an OpenLayers Overlay.
 */
import { computed } from 'vue'

const props = defineProps({
  candidate: { type: Object, required: true },
  compact: { type: Boolean, default: false },
  subject: { type: Boolean, default: false },
  // The target's raw model prediction (CatBoost anchor, EBM fallback) — the
  // reference the derived-price delta is colored against.
  referencePrice: { type: Number, default: null },
  // RED-262: which completeness-gate pool mode produced the main analyze —
  // highlights the matching Kaalutud keskmine row on the subject card.
  activePoolMode: { type: String, default: 'off' },
})

function formatPrice(value) {
  if (value == null) {
    return '–'
  }
  return new Intl.NumberFormat('et-EE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const formatNumber = (value, digits = 2) => {
  if (value == null) {
    return '–'
  }
  return Number(value).toFixed(digits)
}

const formatMeters = value => {
  if (value == null) {
    return '–'
  }
  return value >= 1000 ? `${(value / 1000).toFixed(2)} km` : `${Math.round(value)} m`
}

const pricePerSqm = c => {
  if (!c.tehingusumma || !c.korteri_pind) {
    return null
  }
  return c.tehingusumma / c.korteri_pind
}

const title = computed(() =>
  props.subject
    ? props.candidate.label || 'Hinnatav objekt'
    : props.candidate.address || `#${props.candidate.id}`,
)

const rows = computed(() => {
  const c = props.candidate

  const signedPct = v => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)} %`
  const pctColor = v =>
    v == null || Math.abs(v) < 0.05 ? 'text-gray-400' : v > 0 ? 'text-green-600' : 'text-red-600'

  if (props.subject) {
    // Delta of a weighted average vs the model's raw prediction — the quick
    // "how far does this pool mode pull the result" readout.
    const vsPrediction = value =>
      value != null && c.predicted_value
        ? ((value - c.predicted_value) / c.predicted_value) * 100
        : null

    const out = []
    if (c.manual_valuation != null) {
      out.push(['Käsitsi hinnatud', formatPrice(c.manual_valuation)])
    }
    out.push(['Mudeli ennustus', formatPrice(c.predicted_value)])
    if (c.catboost_prediction != null && c.catboost_model !== 'ebm') {
      out.push(['CatBoost prognoos', formatPrice(c.catboost_prediction)])
    }
    // RED-262: Kaalutud keskmine under every completeness-gate pool mode side
    // by side (the active mode is marked ●) — pool-mode impact without
    // re-filtering. Falls back to the single legacy row when absent.
    const byMode = c.weighted_average_by_pool_mode
    const waRow = (label, value) => {
      const delta = vsPrediction(value)
      return [
        label,
        formatPrice(value),
        delta != null ? signedPct(delta) : null,
        pctColor(delta),
      ]
    }
    if (byMode && typeof byMode === 'object') {
      const modeLabels = [
        ['off', 'Kaalutud keskmine (kõik)'],
        ['imputed', 'Kaalutud keskmine (rik+imputed)'],
        ['observed', 'Kaalutud keskmine (rikastatud)'],
      ]
      for (const [mode, label] of modeLabels) {
        out.push(
          waRow(mode === props.activePoolMode ? `● ${label}` : label, byMode[mode]),
        )
      }
    } else {
      out.push(waRow('Kaalutud keskmine', c.weighted_average))
    }
    return out
  }

  const ppsqm = pricePerSqm(c)
  // The EBM's opinion of the candidate vs what it actually sold for.
  const ebmDelta =
    c.ebm_prediction != null && c.tehingusumma
      ? ((c.ebm_prediction - c.tehingusumma) / c.tehingusumma) * 100
      : null
  // Derived price vs the target's raw model prediction (CatBoost anchor).
  const derivedDelta =
    c.derived_price != null && props.referencePrice
      ? ((c.derived_price - props.referencePrice) / props.referencePrice) * 100
      : null
  // Row shape: [label, value, deltaText?, deltaClass?] — the delta renders as a
  // sign-colored suffix (green raise / red cut / gray ≈ neutral).
  const base = [
    ['Hind', formatPrice(c.tehingusumma)],
    ['€/m²', ppsqm ? formatPrice(ppsqm) : '–'],
    c.derived_price != null
      ? [
          'Tuletatud hind',
          formatPrice(c.derived_price),
          derivedDelta != null ? signedPct(derivedDelta) : null,
          pctColor(derivedDelta),
        ]
      : ['Tuletatud hind', '–'],
    c.ebm_prediction != null
      ? [
          'EBM hind',
          formatPrice(c.ebm_prediction),
          ebmDelta != null ? signedPct(ebmDelta) : null,
          pctColor(ebmDelta),
        ]
      : ['EBM hind', '–'],
    ['Kaugus', formatMeters(c.geo_distance_m)],
    ['Koondskoor', formatNumber(c.score, 3)],
  ]

  if (props.compact) {
    return base
  }

  return [
    ...base,
    ['Pindala', c.korteri_pind ? `${formatNumber(c.korteri_pind, 1)} m²` : '–'],
    ['Kuupäev', c.kpv || '–'],
    ['Tunnuste kaugus', formatNumber(c.embed_distance, 3)],
    ['Hinnatrahv', formatNumber(c.price_penalty, 3)],
    ['Rik. trahv', formatNumber(c.enrichment_penalty, 2)],
  ]
})

// RED-262 completeness chip: how many of the five condition families this
// candidate can display, and how many of those are AI-imputed. Null when the
// API payload lacks field_values (older responses / flag off).
const completeness = computed(() => {
  const fv = props.candidate?.field_values
  if (!fv || typeof fv !== 'object' || props.subject) {
    return null
  }
  const families = Object.values(fv)
  const imputed = families.filter(f => f?.imputed).length
  return { known: families.length, total: 5, imputed }
})
</script>

<template>
  <div class="text-sm" :class="compact ? 'w-56' : 'w-72'">
    <!-- Header: title + district + selected/rank badge -->
    <div class="flex items-start justify-between gap-2 mb-2">
      <div class="min-w-0">
        <div class="font-semibold text-gray-800 leading-tight truncate">{{ title }}</div>
        <div v-if="!subject && candidate.asum" class="text-xs text-gray-400 truncate">
          {{ candidate.asum }}
        </div>
      </div>
      <span
        v-if="subject"
        class="shrink-0 text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-sm"
      >Objekt</span>
      <span
        v-else-if="candidate.was_selected"
        class="shrink-0 text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-sm"
      >Valitud #{{ candidate.rank }}</span>
      <span v-else class="shrink-0 text-[10px] text-gray-400">#{{ candidate.rank }}</span>
    </div>

    <!-- Stats grid -->
    <div class="grid grid-cols-2 gap-x-3 gap-y-1">
      <div v-for="[label, value, delta, deltaClass] in rows" :key="label" class="min-w-0">
        <div class="text-[11px] text-gray-400 leading-none">{{ label }}</div>
        <div class="font-medium text-gray-700 truncate">
          {{ value }}<span v-if="delta" class="text-xs ml-1" :class="deltaClass">{{ delta }}</span>
        </div>
      </div>
    </div>

    <!-- RED-262: condition-data completeness (5 Kohandustabel families) -->
    <div
      v-if="completeness"
      class="mt-2 text-[11px]"
      :class="completeness.known === completeness.total ? 'text-emerald-600' : 'text-amber-600'"
      title="Mitu seisukorravälja viiest (hoone/ruumide seisukord, parkimine, panipaik, rõdu) on kuvatavad; AI = neist tuletatud väärtused"
    >
      Andmed: {{ completeness.known }}/{{ completeness.total }}<span v-if="completeness.imputed > 0"> ({{ completeness.imputed }} AI)</span>
    </div>
  </div>
</template>
